import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MessageCircle, Phone, Mail, Sparkles } from "lucide-react";
import { openExternal } from "@/lib/openExternal";

type Channel = {
  key: "ai" | "email" | "whatsapp" | "messenger";
  url?: string;
  action?: () => void;
  title: string;
  body: string;
  cta: string;
  Icon: typeof MessageCircle;
  // Brand-tinted gradient using semantic tokens + brand colors. We keep the
  // CTA pill on a semantic surface so it adapts to the design system.
  accentClass: string;
  iconWrapClass: string;
};

export function ConnectBand() {
  const { data: settings } = useQuery({
    queryKey: ["site_settings_connect"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("key,value");
      return Object.fromEntries((data ?? []).map((r) => [r.key, r.value])) as Record<string, string>;
    },
    staleTime: 60_000,
  });

  const channels: Channel[] = [
    {
      key: "ai",
      action: () => window.dispatchEvent(new CustomEvent("open-ai-chat")),
      title: "Ask our AI assistant",
      body: "Get instant answers about our projects, donations, 501(c)(3) status, and how to get involved.",
      cta: "Start chat",
      Icon: Sparkles,
      accentClass: "from-primary to-primary",
      iconWrapClass: "bg-primary text-primary-foreground",
    },
    {
      key: "email",
      url: "mailto:contact@raisesrilanka.org",
      title: "Email us",
      body: "Reach the team at contact@raisesrilanka.org for partnerships, press, or any question.",
      cta: "Send an email",
      Icon: Mail,
      accentClass: "from-primary to-accent",
      iconWrapClass: "bg-accent text-accent-foreground",
    },
    {
      key: "whatsapp",
      url: settings?.whatsapp_url,
      title: "Chat on WhatsApp",
      body: "Ask a question, sponsor a family, or get help — we usually reply the same day.",
      cta: "Open WhatsApp",
      Icon: Phone,
      accentClass: "from-[#075e54] to-[#25d366]",
      iconWrapClass: "bg-[#25d366] text-white",
    },
    {
      key: "messenger",
      url: settings?.messenger_url,
      title: "Message us on Facebook",
      body: "Talk to a real volunteer in Messenger. We're here for any question, big or small.",
      cta: "Open Messenger",
      Icon: MessageCircle,
      accentClass: "from-[#00b2ff] to-[#006aff]",
      iconWrapClass: "bg-[#0084ff] text-white",
    },
  ];

  const visible = channels.filter((c) => !!c.url || !!c.action);
  if (!visible.length) return null;

  return (
    <section className="container mx-auto px-4 py-20">
      <div className="mx-auto max-w-3xl text-center">
        <span className="text-sm font-semibold uppercase tracking-widest text-primary">
          Give or get in touch
        </span>
        <h2 className="mt-3 font-display text-3xl font-semibold md:text-4xl">
          Four quick ways to reach us
        </h2>
        <p className="mt-2 text-muted-foreground">
          Ask our AI anything, email the team, or message a real volunteer on WhatsApp or Messenger.
        </p>
      </div>

      <div className="mx-auto mt-10 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {visible.map(({ key, url, action, title, body, cta, Icon, accentClass, iconWrapClass }) => {
          const cardClass =
            "group relative block w-full overflow-hidden rounded-2xl border border-border bg-card p-6 text-left shadow-card transition-all hover:-translate-y-1 hover:shadow-soft";
          const inner = (
            <>
              <div
                className={`pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accentClass}`}
                aria-hidden
              />
              <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${iconWrapClass}`}>
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                {cta}
                <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
              </span>
            </>
          );

          if (action) {
            return (
              <button key={key} type="button" onClick={action} className={cardClass}>
                {inner}
              </button>
            );
          }
          return (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openExternal(url!);
              }}
              className={cardClass}
            >
              {inner}
            </a>
          );
        })}
      </div>
    </section>
  );
}
